import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef, useScreenRenderContext } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver } from '@micromag/core/hooks';
import { isImageFilled, isTextFilled, isHeaderFilled, isFooterFilled, getFooterProps } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Layout from '@micromag/element-layout';
import Scroll from '@micromag/element-scroll';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';
import _objectDestructuringEmpty from '@babel/runtime/helpers/objectDestructuringEmpty';
import _extends from '@babel/runtime/helpers/extends';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { Gallery, Container as Container$1 } from '@micromag/transforms/apple-news';

var styles = {"container":"micromag-screen-gallery-feed-container","background":"micromag-screen-gallery-feed-background","emptyImage":"micromag-screen-gallery-feed-emptyImage","emptyCaption":"micromag-screen-gallery-feed-emptyCaption","content":"micromag-screen-gallery-feed-content","image":"micromag-screen-gallery-feed-image","imageContainer":"micromag-screen-gallery-feed-imageContainer","video":"micromag-screen-gallery-feed-video","footer":"micromag-screen-gallery-feed-footer","disabled":"micromag-screen-gallery-feed-disabled","isPlaceholder":"micromag-screen-gallery-feed-isPlaceholder","layout":"micromag-screen-gallery-feed-layout"};

var propTypes = {
  layout: PropTypes.oneOf(['normal', 'reverse']),
  images: PropTypes.oneOfType([PropTypes$1.imageElementsWithCaption, PropTypes$1.imageMedias]),
  withCaptions: PropTypes.bool,
  spacing: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  active: PropTypes.bool,
  type: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'normal',
  images: null,
  withCaptions: false,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  current: true,
  active: true,
  type: null,
  className: null
};
var GalleryFeedScreen = function GalleryFeedScreen(_ref) {
  var layout = _ref.layout,
    images = _ref.images,
    withCaptions = _ref.withCaptions,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    active = _ref.active,
    type = _ref.type,
    className = _ref.className;
  var trackScreenEvent = useTrackScreenEvent(type);
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || active;
  var hasImages = images !== null;
  var imagesCount = hasImages ? images.length : 0;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    imagesLoaded = _useState2[0],
    setImagesLoaded = _useState2[1];
  var ready = imagesLoaded >= imagesCount;
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;
  var scrollingDisabled = !isEdit && transitionDisabled || !current;
  var onImageLoaded = useCallback(function () {
    setImagesLoaded(imagesLoaded + 1);
  }, [imagesLoaded, setImagesLoaded]);
  var isReversed = layout === 'reverse';
  var items = [];
  var editImages = isEdit && imagesCount === 0 ? [null] : images;
  var finalImages = isPlaceholder ? _toConsumableArray(Array(5)) : editImages;
  var _useDimensionObserver = useDimensionObserver(),
    firstImageRef = _useDimensionObserver.ref,
    firstImageRefWidth = _useDimensionObserver.width;
  (finalImages || []).forEach(function (image, index) {
    var finalImage = withCaptions ? image : {
      media: image
    };
    var _ref2 = finalImage || {},
      _ref2$caption = _ref2.caption,
      caption = _ref2$caption === void 0 ? null : _ref2$caption;
    var hasImage = isImageFilled(finalImage);
    var hasCaption = isTextFilled(caption);
    var imageElement = /*#__PURE__*/React.createElement(ScreenElement, {
      key: "image-".concat(index),
      placeholder: "image",
      emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "ebsPd4",
        defaultMessage: [{
          "type": 0,
          "value": "Image"
        }]
      }),
      emptyClassName: styles.emptyImage,
      isEmpty: !hasImage
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.imageContainer,
      ref: index === 0 ? firstImageRef : null
    }, /*#__PURE__*/React.createElement(Visual, Object.assign({}, finalImage, {
      className: styles.image,
      videoClassName: styles.video,
      width: firstImageRefWidth,
      resolution: resolution,
      playing: backgroundPlaying,
      active: active,
      shouldLoad: mediaShouldLoad,
      withoutVideo: isPreview,
      onLoaded: onImageLoaded
    }))));
    var captionElement = null;
    if (withCaptions) {
      var marginTop = !isReversed || index > 0 ? spacing / 2 : 0;
      var marginBottom = isReversed || index < (finalImages || []).length - 1 ? spacing / 2 : 0;
      captionElement = /*#__PURE__*/React.createElement(ScreenElement, {
        key: "caption-".concat(index),
        placeholder: "text",
        placeholderProps: {
          lines: 2
        },
        emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
          id: "6tIigB",
          defaultMessage: [{
            "type": 0,
            "value": "Caption"
          }]
        }),
        emptyClassName: styles.emptyCaption,
        isEmpty: !hasCaption
      }, hasCaption ? /*#__PURE__*/React.createElement("div", {
        className: styles.caption,
        style: {
          marginTop: marginTop,
          marginBottom: marginBottom
        }
      }, /*#__PURE__*/React.createElement(Text, Object.assign({}, caption, {
        className: styles.captionText
      }))) : null);
    }
    if (isReversed) {
      if (withCaptions) {
        items.push(captionElement);
      }
      items.push(imageElement);
    } else {
      items.push(imageElement);
      if (withCaptions) {
        items.push(captionElement);
      }
    }
    if (!isPlaceholder && index < (finalImages || []).length - 1) {
      items.push( /*#__PURE__*/React.createElement("div", {
        key: "spacing-".concat(index),
        style: {
          height: spacing
        }
      }));
    }
  });
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    scrolledBottom = _useState4[0],
    setScrolledBottom = _useState4[1];
  var onScrolledBottom = useCallback(function (_ref3) {
    var initial = _ref3.initial;
    if (initial) {
      trackScreenEvent('scroll', 'Screen');
    }
    setScrolledBottom(true);
  }, [trackScreenEvent]);
  var onScrolledNotBottom = useCallback(function () {
    setScrolledBottom(false);
  }, [setScrolledBottom]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Scroll, {
    disabled: scrollingDisabled,
    onScrolledBottom: onScrolledBottom,
    onScrolledNotBottom: onScrolledNotBottom,
    withShadow: true
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (!isPreview ? viewerTopHeight : 0) + (hasHeader ? spacing / 2 : spacing),
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + spacing / 2
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    key: "header",
    style: {
      paddingBottom: spacing
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, items, !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.footer, _defineProperty({}, styles.disabled, !scrolledBottom)]),
    style: {
      paddingLeft: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingRight: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingTop: spacing
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null))), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
GalleryFeedScreen.propTypes = propTypes;
GalleryFeedScreen.defaultProps = defaultProps;

var GalleryFeedCaptionsScreen = function GalleryFeedCaptionsScreen(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/React.createElement(GalleryFeedScreen, Object.assign({}, props, {
    withCaptions: true
  }));
};

var transform = function transform(newStory, _ref) {
  var images = _ref.images;
  var _Gallery = Gallery(newStory, {
      images: images
    }),
    titleStory = _Gallery.story,
    titleComponent = _Gallery.component;
  var _Container = Container$1(titleStory, _toConsumableArray(titleComponent ? [titleComponent] : [])),
    containerStory = _Container.story,
    containerComponent = _Container.component;
  return _objectSpread(_objectSpread({}, containerStory), {}, {
    components: [].concat(_toConsumableArray(newStory.components || []), _toConsumableArray(containerComponent ? [containerComponent] : []))
  });
};

// eslint-disable-next-line

var transforms = /*#__PURE__*/Object.freeze({
    __proto__: null,
    appleNews: transform
});

var definition = [{
  id: 'gallery-feed',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "6mX2ya",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    }),
    order: 4
  },
  title: defineMessage({
    id: "4bHvRS",
    defaultMessage: [{
      "type": 0,
      "value": "Gallery Feed"
    }]
  }),
  component: GalleryFeedScreen,
  layouts: ['normal'],
  transforms: transforms,
  fields: [{
    name: 'images',
    type: 'visuals',
    label: defineMessage({
      id: "muYwrB",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}, {
  id: 'gallery-feed-captions',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "6mX2ya",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    }),
    order: 4
  },
  title: defineMessage({
    id: "XTJGzY",
    defaultMessage: [{
      "type": 0,
      "value": "Gallery feed with captions"
    }]
  }),
  component: GalleryFeedCaptionsScreen,
  layouts: ['normal', 'reverse'],
  transforms: transforms,
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'normal',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'images',
    type: 'visuals-with-caption',
    theme: {
      caption: {
        textStyle: 'text'
      }
    },
    label: defineMessage({
      id: "T9NZPQ",
      defaultMessage: [{
        "type": 0,
        "value": "Images"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { GalleryFeedCaptionsScreen, GalleryFeedScreen, definition as default };
